import {CheckIcon, CopyIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, Link} from '@primer/react'
import {useCallback, useEffect, useState, useTransition} from 'react'
import styles from './CommandPicker.module.css'

export interface InstallationMethod {
  key: string
  label: string
  command: string
  os?: 'macos' | 'windows' | 'linux'
}

export interface CommandPickerFooter {
  text: string
  href: string
}

interface CommandPickerProps {
  installationMethods: InstallationMethod[]
  defaultInstallationMethod?: string
  footer?: CommandPickerFooter
  analytics?: {
    category: string
    label: string
  }
}

type DetectedOS = 'macos' | 'windows' | 'linux'

function detectOS(): DetectedOS {
  if (typeof navigator === 'undefined') return 'linux'
  const ua = navigator.userAgent?.toLowerCase() ?? ''
  if (ua.includes('mac')) return 'macos'
  if (ua.includes('win')) return 'windows'
  return 'linux'
}

const OS_FALLBACK_KEYS: Record<DetectedOS, string> = {
  macos: 'homebrew',
  windows: 'winget',
  linux: 'script',
}

/**
 * Resolves which installation method to select by default.
 *
 * Priority:
 * 1. If `explicitDefault` is an explicit key (not "auto") and matches a method → use it.
 * 2. OS-based detection (triggered when explicitDefault is "auto" or omitted):
 *    a. Look for a method with an `os` field matching the detected OS
 *       (e.g., `{ key: 'npm', os: 'macos', ... }` on a Mac).
 *    b. Look for the conventional package manager key for the OS
 *       ("homebrew" on macOS, "winget" on Windows, "script" on Linux/other).
 * 3. Fall back to the first method in the array.
 */
function resolveDefaultMethod(methods: InstallationMethod[], explicitDefault?: string): InstallationMethod {
  const first = methods[0] as InstallationMethod

  if (explicitDefault && explicitDefault !== 'auto') {
    return methods.find(m => m.key === explicitDefault) ?? first
  }

  const os = detectOS()

  // Look for a method that explicitly declares it's for this OS
  const osFieldMatch = methods.find(m => m.os === os)
  if (osFieldMatch) return osFieldMatch

  // Fall back to the conventional key for this OS
  const fallbackMatch = methods.find(m => m.key === OS_FALLBACK_KEYS[os])
  if (fallbackMatch) return fallbackMatch

  return first
}

export function CommandPicker({installationMethods, defaultInstallationMethod, footer, analytics}: CommandPickerProps) {
  const resolvedDefault = resolveDefaultMethod(installationMethods, defaultInstallationMethod)

  const [selectedMethod, setSelectedMethod] = useState<InstallationMethod>(resolvedDefault)
  const [hasCopied, setHasCopied] = useState(false)
  const [, startTransition] = useTransition()

  const handleCopy = useCallback(async () => {
    if (
      typeof navigator === 'undefined' ||
      !navigator.clipboard ||
      typeof navigator.clipboard.writeText !== 'function'
    ) {
      return
    }

    try {
      await navigator.clipboard.writeText(selectedMethod.command)
      setHasCopied(true)
    } catch {
      // Silently ignore clipboard errors
    }
  }, [selectedMethod.command])

  useEffect(() => {
    if (hasCopied) {
      const timer = setTimeout(() => {
        startTransition(() => setHasCopied(false))
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [hasCopied, startTransition])

  if (installationMethods.length === 0) {
    return null
  }

  return (
    <div>
      <div className={styles.CommandPicker} data-testid="command-picker">
        {installationMethods.length > 1 && (
          <ActionMenu>
            <ActionMenu.Button
              className={styles.MethodButton}
              data-testid="command-picker-method-button"
              aria-label={`Installation method: ${selectedMethod.label}`}
            >
              {selectedMethod.label}
            </ActionMenu.Button>
            <ActionMenu.Overlay>
              <ActionList selectionVariant="single">
                {installationMethods.map(method => (
                  <ActionList.Item
                    key={method.key}
                    selected={method.key === selectedMethod.key}
                    onSelect={() => setSelectedMethod(method)}
                    data-analytics-event={
                      analytics
                        ? JSON.stringify({
                            category: analytics.category,
                            action: `select-${method.key}`,
                            label: analytics.label,
                          })
                        : undefined
                    }
                  >
                    {method.label}
                  </ActionList.Item>
                ))}
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        )}

        <code className={styles.Command} data-testid="command-picker-command">
          $ {selectedMethod.command}
        </code>

        <Button
          className={styles.CopyButton}
          variant="primary"
          onClick={handleCopy}
          aria-label={`Copy command: ${selectedMethod.command}`}
          leadingVisual={hasCopied ? CheckIcon : CopyIcon}
          data-testid="command-picker-copy-button"
          data-analytics-event={
            analytics
              ? JSON.stringify({
                  category: analytics.category,
                  action: `copy-${selectedMethod.key}`,
                  label: analytics.label,
                })
              : undefined
          }
        >
          Copy
        </Button>

        <span className="sr-only" role="status">
          {hasCopied ? 'Copied to clipboard' : ''}
        </span>
      </div>

      {footer && (
        <div className={styles.Footer}>
          <Link href={footer.href} className="Link--inTextBlock">
            {footer.text}
          </Link>
        </div>
      )}
    </div>
  )
}
